/**
 * 문의 CTA 클릭 ↔ 문의폼 제출을 잇는 익명 클릭 식별자(`tracking_id`).
 *
 * UTM 과 목적이 다르므로 `utmHelper` 와 파일을 나눈다.
 * - UTM 은 "어디서 왔나"(유입 출처)를 URL/스토리지에서 **읽는** 값 → 몇 번 읽어도 같다(idempotent).
 * - `tracking_id` 는 "이 클릭"을 가리키는 값 → **클릭할 때마다 새로 생성**한다(저장하지 않는다).
 *
 * 흐름: 클릭 시 1개 생성 → ① 클릭 분석 이벤트 prop ② 문의폼 URL 에 **같은 값**을 실어 보내고,
 * 문의폼(CRM)이 제출 데이터에 그대로 저장한다. 사후에 두 쪽을 대조하면 UTM 이 중간 이동에서
 * 끊긴 경우에도 "클릭 → 실제 제출" 전환을 이을 수 있다.
 *
 * 개인정보: `zepDeviceId` 같은 기존 식별자를 재사용하지 않는 1회성 랜덤 UUID 다.
 * 저장소에 남기지 않으므로 클릭 단위를 넘어 사용자를 따라다니지 않는다.
 */

/** 문의폼 URL 로 실어 보낼 때 쓰는 파라미터 키. CRM 이 이 이름으로 캡처한다. */
export const TRACKING_ID_PARAM = 'tracking_id';

/** `createTrackingId()` 가 만드는 v4 UUID 형태. */
const TRACKING_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * URL 에서 읽은 값이 **우리가 만든 tracking_id** 인지 확인하고 통과시킨다.
 *
 * `tracking_id` 는 공개 URL 로 오가므로 누구나 임의 문자열을 넣을 수 있다. 검증 없이 승계하면
 * 그 값이 그대로 분석 이벤트·GTM `dataLayer`·외부 문의폼까지 흘러간다 —
 * 데이터 오염은 물론, 링크에 담긴 이메일·토큰 같은 값이 그대로 유출될 수 있다.
 * (GTM 허용목록이 `tracking_id` 를 통과시키는 전제가 "익명 UUID" 라는 점이 여기서 깨진다)
 *
 * 형식이 맞지 않으면 `undefined` 를 돌려주고, 호출부는 새 id 를 만들거나 전달을 생략한다.
 */
export const parseTrackingId = (value: unknown): string | undefined =>
  typeof value === 'string' && TRACKING_ID_PATTERN.test(value)
    ? value
    : undefined;

/**
 * 클릭 1회를 가리키는 UUID 를 만든다.
 *
 * `@zep/session-id` 의 `safeRandomUUID()` 와 같은 폴백 전략이지만, quiz-app 은
 * `@zep/utils` 표면을 최소로 유지한다(그 배럴은 `sideEffects` 표시가 없어 dateHelpers·
 * EventEmitter 까지 랜딩/프라이싱 번들로 끌려온다). 같은 이유로 `shared/utils/fingerprint.ts`
 * 도 로컬 구현을 유지하고 있다.
 */
export const createTrackingId = (): string => {
  const cryptoApi = globalThis.crypto;

  // crypto.randomUUID 는 secure context 밖·구형 인앱 웹뷰에서 `crypto` 는 있어도
  // 없을 수 있어(PLAY-APP-B64) 존재 확인 후에만 호출한다.

  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  const getRandomByte =
    typeof cryptoApi?.getRandomValues === 'function'
      ? () => cryptoApi.getRandomValues(new Uint8Array(1))[0]
      : () => Math.floor(Math.random() * 256);

  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, char => {
    const c = Number(char);
    return (c ^ (getRandomByte() & (15 >> (c / 4)))).toString(16);
  });
};

/**
 * 해시 뒤 파라미터 구간(`#<formId>?a=b`)에 `tracking_id` 를 넣는다.
 * 이미 있으면 이번 클릭 값으로 덮어쓴다.
 */
const setTrackingIdInHash = (hash: string, trackingId: string): string => {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const queryStart = raw.indexOf('?');
  const target = queryStart >= 0 ? raw.slice(0, queryStart) : raw;
  const params = new URLSearchParams(
    queryStart >= 0 ? raw.slice(queryStart + 1) : '',
  );

  params.set(TRACKING_ID_PARAM, trackingId);
  return `#${target}?${params.toString()}`;
};

/**
 * 문의폼 URL 에 `tracking_id` 를 붙여 반환한다.
 *
 * **해시가 있으면 해시 뒤에 붙인다.** CRM 공개 문의폼(promotion.zep.us/sales-form)은
 * CloudFront 가 쿼리스트링을 떨어뜨려 폼을 `#<formId>` 로 지목하고 파라미터도 해시 뒤에
 * 싣는 규약이라, 쿼리에 넣으면 폼까지 도달하지 못한다. CRM 은 `location.search` 와
 * 해시 뒤 구간을 모두 읽으므로, 해시가 없는 폼(현행 salesmap 등)은 일반 쿼리로 붙인다.
 *
 * `appendUTMParams` 와 같은 안전 규약을 따른다 — SSR 이거나 URL 파싱에 실패하면 원본
 * 문자열을 그대로 돌려줘 링크 자체는 깨지지 않게 한다. `trackingId` 가 없으면(예: 클릭
 * 없이 인라인 임베드된 폼) 아무것도 하지 않는다.
 */
export const appendTrackingId = (
  url: string,
  trackingId: string | undefined,
): string => {
  if (typeof window === 'undefined') return url;
  if (!trackingId) return url;

  try {
    const urlObj = new URL(url, window.location.origin);

    if (urlObj.hash) {
      urlObj.hash = setTrackingIdInHash(urlObj.hash, trackingId);
    } else {
      urlObj.searchParams.set(TRACKING_ID_PARAM, trackingId);
    }

    return urlObj.toString();
  } catch {
    return url;
  }
};
